import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Sun, Leaf, Snowflake, Sprout, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEASON_OPTIONS = [
  { id: null, label: "Auto (recommended)", icon: Globe, color: "#FFFFFF" },
  { id: "summer", label: "Summer Off-Season", icon: Sun, color: "#FBBF24" },
  { id: "fall", label: "Fall Pre-Season", icon: Leaf, color: "#F97316" },
  { id: "winter", label: "Winter In-Season", icon: Snowflake, color: "#60A5FA" },
  { id: "spring", label: "Spring Playoffs", icon: Sprout, color: "#34D399" },
];

/**
 * SeasonSettings
 * --------------
 * Lets the athlete pick hemisphere + override the auto-detected season.
 * Lives on the Profile page.
 */
const SeasonSettings = () => {
  const [hemisphere, setHemisphere] = useState("north");
  const [override, setOverride] = useState(null);
  const [autoSeason, setAutoSeason] = useState("fall");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/season/current`, { headers });
        setHemisphere(res.data.season.hemisphere || "north");
        setOverride(res.data.season.is_override ? res.data.season.name : null);
        setAutoSeason(res.data.season.auto_season || "fall");
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/season/settings`, patch, { headers });
      setAutoSeason(res.data.season.auto_season);
      toast.success("Seasonal settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const changeHemisphere = (h) => {
    setHemisphere(h);
    save({ hemisphere: h });
  };

  const changeOverride = (newOverride) => {
    setOverride(newOverride);
    save({ season_override: newOverride });
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-xs font-mono text-white/40 py-4">
        <Loader2 className="w-3 h-3 animate-spin" /> LOADING SEASON SETTINGS...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="season-settings">
      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-3 flex items-center gap-2">
          <Globe className="w-3 h-3" /> HEMISPHERE
        </p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { id: "north", label: "Northern (US / Europe / Asia)" },
            { id: "south", label: "Southern (AUS / NZ / S. America)" },
          ].map((h) => {
            const active = hemisphere === h.id;
            return (
              <button
                key={h.id}
                onClick={() => changeHemisphere(h.id)}
                disabled={saving}
                data-testid={`hemisphere-${h.id}`}
                className="text-left p-3 border transition-all"
                style={{
                  background: active ? "rgba(255,255,255,0.06)" : "transparent",
                  borderColor: active ? "#FFFFFF" : "rgba(255,255,255,0.08)",
                }}
              >
                <p className="text-xs font-mono uppercase tracking-wider text-white">
                  {h.id.toUpperCase()}
                </p>
                <p className="text-[10px] font-mono text-white/40 mt-0.5">{h.label}</p>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/40 mb-1 flex items-center gap-2">
          CURRENT SEASON
        </p>
        <p className="text-[10px] font-mono text-white/30 mb-3">
          Auto-detected based on today&apos;s date and your hemisphere:
          <span className="text-white ml-1">{autoSeason.toUpperCase()}</span>.
          Override only if you&apos;re training on a non-standard calendar (AAU, international, etc).
        </p>
        <div className="space-y-2">
          {SEASON_OPTIONS.map((o) => {
            const active = override === o.id;
            const Icon = o.icon;
            return (
              <button
                key={o.id || "auto"}
                onClick={() => changeOverride(o.id)}
                disabled={saving}
                data-testid={`season-opt-${o.id || "auto"}`}
                className="w-full text-left p-3 border flex items-center gap-3 transition-all"
                style={{
                  background: active ? `${o.color}10` : "transparent",
                  borderColor: active ? o.color : "rgba(255,255,255,0.08)",
                }}
              >
                <Icon className="w-4 h-4 flex-shrink-0" style={{ color: active ? o.color : "rgba(255,255,255,0.4)" }} />
                <span
                  className="text-xs font-mono uppercase tracking-wider"
                  style={{ color: active ? o.color : "rgba(255,255,255,0.6)" }}
                >
                  {o.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SeasonSettings;
